<script setup lang="ts">
import RowCheckBox from '@/components/atom/RowCheckBox.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import { i18n } from '@/i18n';
import { computed, inject, Ref, ref, toRef } from 'vue';
import { InventoryDashboardViewModel } from '../pages/inventoryDashboardViewModel';
import { HeaderList, TableType } from '@/coreDomainsModule/table';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { LIST_VIEW_SECTION_INJECTION_KEY } from '../constants';

const t = i18n.t;
const vm = new InventoryDashboardViewModel();

const props = defineProps<{
  businesses: any[];
  checkedBusinessId: string[];
  tableType: TableType;
  fullHeaderOptions: {
    key: string;
    width: number;
  }[];
  headerList: string[];
  isAggregateBusiness: boolean;
}>();
const emits = defineEmits<{
  (e: 'onToggleHeader', headerKey: string, isHide: boolean): void;
  (e: 'onCheckedRow', businessId: string, isChecked: boolean): void;
}>();

const tableWidthInfo = inject<Ref<Record<string, number | undefined>>>(LIST_VIEW_SECTION_INJECTION_KEY);
if (!tableWidthInfo) throw new Error('tableWidthInfoがない');

const tableTypeRef = toRef(props, 'tableType');
const tableRef = ref<HTMLElement | null>(null);

const { onMousedownResizer } = useColumnResizer({
  tableType: tableTypeRef,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const modifiableHeaders = computed(
  (): HeaderList =>
    props.fullHeaderOptions
      .filter((h) => !['checkbox', 'detail', 'headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: vm.convertHeaderKeyToDisplayValue(h.key),
        isShown: props.headerList.includes(h.key),
      }))
);

const gridTemplateColumns = computed(() =>
  props.fullHeaderOptions
    .filter((header) => props.headerList.includes(header.key))
    .map((h) => {
      const saved = tableWidthInfo.value[h.key];
      return saved ? `${saved}px` : `minmax(${h.width}px, ${h.width}fr)`;
    })
);
</script>
<template>
  <EzTable ref="tableRef" :grid-template-columns="gridTemplateColumns" sticky-header is-vertical-scrollable class="item__table__container">
    <template #header>
      <template v-for="header in headerList.slice(0, -1)" :key="header">
        <EzHeader v-if="header === 'checkbox'" class="item__table__header"><RowCheckBox /></EzHeader>
        <EzHeader v-else-if="header === 'detail'" :content-padding="0" force-center class="item__table__header" />
        <EzHeader v-else use-resizer class="item__table__header" @resizer="(e) => onMousedownResizer(e, header)">
          <div class="item__table__header__inner">
            {{ vm.convertHeaderKeyToDisplayValue(header) }}
            <!-- <ArrowIcon
                    :asc="convertHeaderKeyToSortKey(header) === sortTarget ? sortType === 'ASC' : false"
                    @on-click="() => onClickSortArrowHandler(header)"
                  />-->
          </div>
        </EzHeader>
      </template>
      <EzHeader sticky="right">
        <TableHeaderModifyButton
          :modifiable-header-list="modifiableHeaders"
          @toggle="(key, isHide) => emits('onToggleHeader', key, isHide)"
        />
      </EzHeader>
    </template>
    <template #body>
      <EzTableRow v-for="(data, index1) in props.businesses" :key="index1" :grid-template-columns="gridTemplateColumns">
        <template v-for="header in headerList.slice(0, -1)" :key="header">
          <EzCell
            v-if="header === 'checkbox'"
            :is-highlighted="props.checkedBusinessId.includes(data.businessId)"
            class="item__table__cell"
          >
            <RowCheckBox @change-status="(isChecked) => emits('onCheckedRow', data.businessId, isChecked)" />
          </EzCell>
          <EzCell
            v-else-if="header === 'detail'"
            :content-padding="0"
            :is-highlighted="props.checkedBusinessId.includes(data.businessId)"
            class="item__table__cell"
          >
            <RouterLink
              v-if="data.businessId"
              :to="
                props.isAggregateBusiness
                  ? { name: 'recommend-order', params: { id: data.businessId } }
                  : { name: 'customer-forecast-timeline', params: { defaultBusinessId: data.businessId } }
              "
              target="_blank"
            >
              <TextButton class="table-detail-button" button-style="btn-secondary" :on-click="() => {}">
                {{ t('詳細') }}
              </TextButton>
            </RouterLink>
          </EzCell>
          <EzCell
            v-else-if="
              ['aggregateBusinessResponsibleUsers', 'baseBusinessResponsibleUsers', 'forecastForReceiptResponsibleUsers'].includes(header)
            "
            :is-highlighted="props.checkedBusinessId.includes(data.businessId)"
            class="item__table__cell"
          >
            {{ vm.convertUserAccountToDisplayValue(data[header]) }}
          </EzCell>
          <EzCell
            v-else-if="vm.numberHeaderKeys.includes(header)"
            is-number
            :is-highlighted="props.checkedBusinessId.includes(data.businessId)"
            class="item__table__cell"
          >
            {{ vm.convertNumberStringToDisplayValue(header, data[header]) }}
          </EzCell>
          <EzCell v-else :is-highlighted="props.checkedBusinessId.includes(data.businessId)" class="item__table__cell">
            {{ data[header] ?? '--' }}
          </EzCell>
        </template>
        <EzCell />
      </EzTableRow>
    </template>
  </EzTable>
</template>
<style lang="scss" scoped>
.item__table {
  &__container {
    position: relative;
  }
  &__header {
    width: 100%;
    height: 36px;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
    &__inner {
      width: 100%;
      display: grid;
      grid-template-columns: max-content max-content;
      justify-content: space-between;
      align-items: center;
    }
  }
  &__cell {
    height: 24px;
    font-weight: 400;
    font-size: 12px;
    line-height: 1;
  }
}
.table-detail-button {
  display: grid;
  justify-content: center;
  align-items: center;
  font-family: Noto Sans JP;
  padding: unset;
  height: 16px;
  width: 56px;
  background-color: $semanticWhite;
  color: $semanticPrimary;
  border: 1px solid $semanticPrimary;
  font-size: 12px;
  line-height: 12px;
  font-weight: 700;
}
</style>
